// Booking a visit, what it does (BL-189): every rule of the booking form (technical design §6.5) in one
// place, so Book.tsx's form (on a call, and Riverstone's Book a job) and Blueprint's Book a visit
// (BookPage.tsx) book the same way: after hours, a collections hold, capacity and its changes, the
// confirmation text, the call kept on the job, the kept booking (QA-723), a refusal naming its field
// (QA-720) and "already booked" (QA-698). How each draws it is its own.

import { useCallback, useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { uuidv7 } from '@flowology/kernel';
import { client } from '../api.ts';
import {
  alreadyBooked,
  blockers,
  bookInput,
  bookSoldInput,
  confirmationText,
  dayLabel as dayLabelOf,
  emptyDraft,
  firstOpenDay,
  hourNow,
  overlaps,
  today as todayIn,
  windowOver,
  zonedInstant,
  type BoardTech,
  type Draft,
  startLocation,
  summaryFromNotes,
  capNote,
  capReason,
  capWarning,
  soonestFit,
  weekGrid,
  weekPage,
  weekPageOf,
  windowCapOn,
  WEEK_PAGES,
  type WeekCell,
  type WeekRead,
  type WindowCap,
  type TagOption,
  bookRefusal,
  draftHasInput,
  typesFor,
  type BookField,
  type AlreadyBooked,
} from '../book-model.ts';
import { forgetBooking, keepBooking, keptBooking } from '../book-draft-cache.ts';
import { setupRights } from '../settings/setup-model.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { useMirror } from '../shared/mirror.ts';
import { useDesign } from '../blueprint/design.ts';
import {
  asksWater,
  NO_WATER_ANSWER,
  WATER_EMERGENCY_REASON,
  waterUrgency,
  waterWords,
  type WaterAnswer,
} from '../calls/water-model.ts';
import { emptyCustomerForm, type CustomerForm } from '../shared/new-customer-model.ts';
import type { NavProps } from '../Nav.tsx';
import { alertsOf, warrantyLine, type LocationRecords } from '../records/records-model.ts';
import { phoneText, visitTextTo, type BookStart, type Customer } from '../types.ts';

export interface Named {
  id: string;
  name: string;
  /** The name its customers read (BL-052): its official name, else the company's. */
  customerName?: string;
}
export interface JobType extends Named {
  /** None: for any department (QA-721). */
  businessUnitId: string | null;
}
export interface Booked {
  jobId: string;
  jobNumber: string;
  appointmentId: string;
  texted: boolean;
}

/**
 * The booking form's state and what it does. With `onBooked` the caller says what was booked; without
 * it, the form does (booked).
 */
export function useBookForm({
  nav,
  start,
  timeZone,
  onBooked,
}: {
  nav: NavProps;
  start: BookStart | null;
  timeZone: string;
  onBooked?: ((b: Booked) => void) | undefined;
}) {
  const today = todayIn(timeZone);
  const nowH = hourNow(timeZone);
  // While the company mirrors ServiceTitan, its jobs and visits are booked there (st-mirror §2)
  const mirroring = useMirror();
  // Booking opens on the first day with a window left (ux #10): tomorrow, once today's have all started.
  const opens = firstOpenDay(today, nowH);
  // A plain booking left unsent is kept for this tab (QA-723): Book a visit opens on it again. One
  // started from a call, a lead, an estimate or a customer is that one's own.
  const plain = !start && !onBooked;
  const [restored] = useState(() => (plain ? keptBooking(nav.tenant) : null));
  const [d, setD] = useState<Draft>(() => {
    if (!restored) return { ...emptyDraft(opens), callId: start?.callId ?? null };
    const k = restored.draft;
    // a day gone by, or a window that is over since, is picked again (the one under way stays, QA-829)
    if (k.date < opens) return { ...k, date: opens, window: null };
    return k.window && windowOver(k.date, today, k.window, nowH) ? { ...k, window: null } : k;
  });
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [query, setQuery] = useState(restored?.query ?? '');
  // the field the last refusal named, marked until it is changed (QA-720)
  const [bad, setBad] = useState<BookField | null>(null);
  const invalid = (f: BookField) =>
    bad === f ? { 'aria-invalid': true as const, 'aria-describedby': 'book-refused' } : {};
  const [found, setFound] = useState<{ id: string; displayName: string; city: string | null }[]>([]);
  const [units, setUnits] = useState<Named[]>([]);
  const [types, setTypes] = useState<JobType[]>([]);
  // whether the job types have been read: then an empty list means the company has none (QA-723)
  const [typesRead, setTypesRead] = useState(false);
  const [techs, setTechs] = useState<Named[]>([]);
  const [board, setBoard] = useState<BoardTech[]>([]);
  // BL-057, BL-133: open capacity by window, for the department and job type chosen, a week a read:
  // each page's answer under its key (the day, department, job type and re-read it was for); null failed
  const [reads, setReads] = useState<Record<string, WeekRead | null>>({});
  const asked = useRef(new Set<string>());
  const [page, setPage] = useState(0); // the week shown: 0 is the week from today
  // water running (calls round): for a leak or a burst, in Blueprint, two questions decide how soon
  const bpForm = useDesign() === 'blueprint';
  const [water, setWater] = useState<WaterAnswer>(NO_WATER_ANSWER);
  const [capTry, setCapTry] = useState(0); // re-read after the office changes a window
  const [newLoc, setNewLoc] = useState<{ line1: string; city: string } | null>(null);
  const [estimate, setEstimate] = useState(start?.estimate ?? null);
  const [soldAt, setSoldAt] = useState<string | null>(null); // the address the estimate was for
  const [booked, setBooked] = useState<Booked | null>(null);
  // The company can't text yet (QA-324): said instead of a confirmation that never went.
  const [textingOff, setTextingOff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // QA-698: the customer already has a visit for this job type near it: book anyway, or open that one
  const [dupe, setDupe] = useState<AlreadyBooked | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  // A customer who isn't on file yet is added right here (QA-323), started from what was typed.
  const [newCustomer, setNewCustomer] = useState<CustomerForm | null>(null);
  // When the form closes (added or cancelled), focus goes back to the customer field it came from.
  const customerField = useRef<HTMLInputElement>(null);
  const patch = (p: Partial<Draft>) => {
    setBad(null);
    setD((x) => ({ ...x, ...p }));
  };
  // The same form, pressed again after a failure, keeps its key: it is never booked twice.
  const save = useSaveKey(JSON.stringify([d, estimate?.id ?? null]));

  // Company set-up: business units, job types, technicians.
  useEffect(() => {
    void Promise.all([
      client.call<{ businessUnits: Named[] }>('businessUnits.list', {}),
      client.call<{ jobTypes: JobType[] }>('jobTypes.list', {}),
      client.call<{ staff: { id: string; displayName: string }[] }>('staff.list', { techniciansOnly: true }),
    ]).then(([u, t, s]) => {
      setUnits(u.businessUnits);
      setTypes(t.jobTypes);
      setTypesRead(true);
      setTechs(s.staff.map((x) => ({ id: x.id, name: x.displayName })));
      setD((x) => (x.businessUnitId ? x : { ...x, businessUnitId: u.businessUnits[0]?.id ?? null }));
    });
  }, []);
  // The confirmation text says whose it is (ux #20): the company's name, as its sign-in page shows it.
  const [company, setCompany] = useState<string | null>(null);
  useEffect(() => {
    client
      .brand(nav.tenant)
      .then((b) => setCompany(b.name))
      .catch(() => setCompany(null));
  }, [nav.tenant]);

  const [hold, setHold] = useState<{ reason: string | null } | null>(null);
  // prefer: the address to open on (a call's), when it's one of theirs
  const pickCustomer = useCallback(async (id: string, prefer?: string | null) => {
    const c = await client.call<Customer>('customers.get', { id });
    setHold(null);
    // A collections hold is a banner while booking, never a block (emergency work goes ahead).
    void client
      .call<{ onHold: boolean; reason: string | null }>('customers.collectionsHold', { customerId: c.id })
      .then((h) => setHold(h.onHold ? { reason: h.reason } : null))
      .catch(() => undefined);
    setCustomer(c);
    setQuery(c.displayName);
    setFound([]);
    const at = startLocation(c.locations, prefer);
    setD((x) => ({
      ...x,
      customerId: c.id,
      locationId: at,
      sendConfirmation: 'method' in visitTextTo(c, at),
    }));
  }, []);

  useEffect(() => {
    if (start?.customerId) void pickCustomer(start.customerId, start.locationId);
    const fromNotes = summaryFromNotes(start?.notes);
    setD((x) => ({
      ...x,
      callId: start?.callId ?? null,
      leadId: start?.lead?.id ?? null,
      ...(start?.lead
        ? { summary: start.lead.summary.slice(0, 4000) }
        : fromNotes && !x.summary
          ? { summary: fromNotes }
          : {}),
      ...(start?.estimate ? { kind: 'service' as const } : {}),
    }));
    // A lead with no customer yet: the new-customer form, with what the lead knows (consent is asked).
    const lead = start?.lead;
    if (lead && !start?.customerId)
      setNewCustomer({
        ...emptyCustomerForm(),
        name: lead.name ?? '',
        mobile: lead.phone ? phoneText(lead.phone) : '',
        email: lead.email ?? '',
      });
    setSoldAt(null);
    setEstimate(start?.estimate ?? null);
    if (start?.estimate)
      void client
        .call<{ address: string }>('jobs.get', { id: start.estimate.jobId })
        .then((j) => setSoldAt(j.address))
        .catch(() => undefined);
  }, [start, pickCustomer]);

  // the kept booking's customer, read again (their details are never kept)
  useEffect(() => {
    if (restored?.draft.customerId) void pickCustomer(restored.draft.customerId, restored.draft.locationId);
  }, [restored, pickCustomer]);

  // QA-723: keep a plain booking while it holds something, until it's booked or emptied
  useEffect(() => {
    if (!plain) return;
    if (booked || !draftHasInput(d, query)) forgetBooking();
    else keepBooking(nav.tenant, { draft: d, query });
  }, [plain, booked, d, query, nav.tenant]);

  // From an estimate, the job goes where the estimate was sold: that location's bill-to applies.
  useEffect(() => {
    const at = customer?.locations.find((l) => `${l.line1}, ${l.city}` === soldAt);
    if (estimate && at) setD((x) => ({ ...x, locationId: at.id }));
  }, [estimate, customer, soldAt]);

  // Type-ahead over every customer (name, number or street).
  useEffect(() => {
    if (!query.trim() || query === customer?.displayName) return setFound([]);
    const t = setTimeout(() => {
      void client
        .call<{ customers: { id: string; displayName: string; city: string | null }[] }>('customers.list', {
          query: query.trim(),
          limit: 8,
        })
        .then((r) => setFound(r.customers))
        .catch(() => setFound([])); // a lost session asks to sign in again (App)
    }, 200);
    return () => clearTimeout(t);
  }, [query, customer]);

  // The day's board, for overlaps with the technicians picked.
  useEffect(() => {
    if (!d.techIds.length) return setBoard([]);
    void client
      .call<{ technicians: BoardTech[] }>('dispatch.board', { date: d.date })
      .then((b) => setBoard(b.technicians))
      .catch(() => setBoard([]));
  }, [d.date, d.techIds]);

  const unitTypes = typesFor(types, d.businessUnitId);
  const wet = bpForm && asksWater(types.find((t) => t.id === d.jobTypeId)?.name, d.summary);
  const waterSaid = wet ? waterWords(waterUrgency(water)) : null;
  // an emergency books after hours or emergency, today, with why it can't wait; today takes today's
  // next window (and undoes an emergency only the water's answer set)
  const answerWater = (w: WaterAnswer) => {
    setWater(w);
    const u = waterUrgency(w);
    const ours = d.afterHours && d.afterHoursReason === WATER_EMERGENCY_REASON;
    if (u === 'emergency')
      patch({
        afterHours: true,
        afterHoursReason: d.afterHoursReason.trim() || WATER_EMERGENCY_REASON,
        date: today,
        window: null,
      });
    else if (u === 'today')
      patch({ ...(ours ? { afterHours: false, afterHoursReason: '' } : {}), date: today, window: null });
    else if (ours) patch({ afterHours: false, afterHoursReason: '', window: null });
    if (u) setPage(0);
  };
  const mayAddTypes = setupRights(nav.roles ?? []).org;
  const windowSpan = d.window
    ? { start: zonedInstant(d.date, d.window[0], timeZone), end: zonedInstant(d.date, d.window[1], timeZone) }
    : null;
  const clashes = overlaps(board, d.techIds, windowSpan);
  // BL-133: the week grid, one capacity.availability read per page (the read takes at most 14 days)
  const capBase = `${today}:${d.businessUnitId ?? ''}:${d.jobTypeId ?? ''}:${capTry}`;
  const readOf = (p: number): WeekRead | null | undefined => reads[`${capBase}#${p}`];
  const grids = Array.from({ length: WEEK_PAGES }, (_, p) => {
    const r = readOf(p);
    return r === undefined ? undefined : weekGrid(today, p, r, { nowHour: nowH, timeZone });
  });
  // the soonest that fits reads on a page at a time, only while the pages before have nothing
  const soon = soonestFit(grids);
  const want = [...new Set([page, ...('need' in soon ? [soon.need] : [])])];
  const wantKey = want.join(',');
  useEffect(() => {
    if (d.afterHours) return;
    for (const p of wantKey.split(',').map(Number)) {
      const key = `${capBase}#${p}`;
      if (asked.current.has(key)) continue;
      asked.current.add(key);
      const { from, to } = weekPage(today, p);
      client
        .call<WeekRead>('capacity.availability', {
          from,
          to,
          ...(d.businessUnitId ? { businessUnitId: d.businessUnitId } : {}),
          ...(d.jobTypeId ? { jobTypeId: d.jobTypeId } : {}),
        })
        .then((r) =>
          setReads((x) => ({
            ...x,
            [key]: {
              jobType: r.jobType,
              ...(r.idealPercent !== undefined ? { idealPercent: r.idealPercent } : {}),
              days: r.days,
            },
          })),
        )
        .catch(() => setReads((x) => ({ ...x, [key]: null }))); // without it, every window is still pickable
    }
  }, [wantKey, capBase, today, d.afterHours, d.businessUnitId, d.jobTypeId]);
  // the chosen window's capacity, from its week's answer
  const dateRead = (() => {
    const p = weekPageOf(today, d.date);
    return p === null ? undefined : readOf(p);
  })();
  const chosenCap = d.window && !d.afterHours ? windowCapOn(dateRead, d.date, d.window, timeZone) : undefined;
  const capWhy = chosenCap ? capReason(chosenCap, dateRead?.jobType ?? null, dateRead?.idealPercent) : null;
  const capWarn = chosenCap ? capWarning(chosenCap, dateRead?.jobType ?? null, dateRead?.idealPercent) : null;
  const pickCell = (c: WeekCell) => {
    patch({ date: c.date, window: c.window });
    setPage(weekPageOf(today, c.date) ?? 0);
  };
  // the job's note when it's booked against capacity (phase 2): it goes with the booking, so it is
  // never lost to the booker's note rights (QA-696), and a retry with the same key adds it once
  const bookNote = capWhy ? capNote(capWhy) : null;
  // a warning is about the booking as it was: once it changes, it no longer stands
  useEffect(() => setDupe(null), [save.key]);
  // capacity.adjust and its undo ask jobs.dispatch, whichever role gives it (QA-681)
  const mayAdjust = (nav.permissions ?? []).includes('jobs.dispatch');
  // the address's site contact when it has one (F-54), else the customer
  const textTo = customer ? visitTextTo(customer, d.locationId) : null;
  // the confirmation is signed with the department's own name when it has one (BL-052)
  const signedBy = units.find((u) => u.id === d.businessUnitId)?.customerName ?? company;
  const stops = blockers(d);
  const dayLabel = dayLabelOf(d.date, today);

  const addLocation = async () => {
    if (!customer || !newLoc) return;
    setAdding(true);
    setAddError(null);
    try {
      const c = await client.call<Customer>('customers.addLocation', {
        customerId: customer.id,
        location: { address: { line1: newLoc.line1.trim(), city: newLoc.city.trim(), country: 'CA' } },
      });
      setCustomer(c);
      patch({ locationId: c.locations.at(-1)!.id });
      setNewLoc(null);
    } catch (e) {
      setAddError(e instanceof RpcError ? e.message : 'Couldn’t add the location. Try again.');
    } finally {
      setAdding(false);
    }
  };

  const book = async (anyway = false) => {
    setBusy(true);
    setError(null);
    try {
      const r: Omit<Booked, 'texted'> = estimate
        ? await client.call<Omit<Booked, 'texted'>>(
            'estimates.bookSold',
            bookSoldInput(d, timeZone, estimate.id, bookNote),
            {
              idempotencyKey: save.key,
            },
          )
        : await client.call<Omit<Booked, 'texted'>>(
            'jobs.book',
            bookInput(d, timeZone, { note: bookNote, checkDuplicate: !anyway }),
            // "Book anyway" is another request: its own key, kept for its retries
            { idempotencyKey: anyway ? `${save.key}:anyway` : save.key },
          );
      setDupe(null);
      let texted = false;
      // No texting account yet (QA-324): nothing is queued, and the screen says it wasn't sent.
      const canText = await client
        .call<{ sms: boolean }>('messages.channels', {})
        .then((c) => c.sms)
        .catch(() => true);
      setTextingOff(!canText);
      if (canText && d.sendConfirmation && textTo && 'method' in textTo && d.window) {
        try {
          await client.call(
            'messages.sendRoutine',
            {
              contactMethodId: textTo.method.id,
              purpose: 'booking_confirmation',
              body: confirmationText({
                firstName: textTo.firstName,
                company: signedBy,
                dayLabel,
                window: d.window,
              }),
              jobId: r.jobId,
              appointmentId: r.appointmentId,
            },
            { idempotencyKey: `${save.key}:text` },
          );
          texted = true;
        } catch {
          // The booking stands; the office sees there was no text.
        }
      }
      if (d.callId)
        await client
          .call('calls.update', { id: d.callId, outcome: 'booked' }, { idempotencyKey: `${save.key}:call` })
          .catch(() => undefined);
      if (onBooked) onBooked({ ...r, texted });
      else setBooked({ ...r, texted });
      save.renew();
    } catch (e) {
      // QA-698: the customer already has this visit booked: book anyway, or open that one
      const already = e instanceof RpcError ? alreadyBooked(e) : null;
      if (already) {
        setBad(null);
        return setDupe(already);
      }
      // A refusal says why, naming the field it found wrong (QA-720); anything else may have reached
      // the server, and the same key makes a retry safe. Everything typed stays as it was (QA-696).
      const r =
        e instanceof RpcError && e.code !== 'offline' && e.code !== 'internal' ? bookRefusal(e) : null;
      setBad(r?.field ?? null);
      setError(r ? r.message : 'Couldn’t book it. Try again; it won’t be booked twice.');
    } finally {
      setBusy(false);
    }
  };

  /** Book another, once one is booked: the department stays. */
  const bookAnother = () => {
    setBooked(null);
    setEstimate(null);
    setCustomer(null);
    setQuery('');
    setD({ ...emptyDraft(opens), businessUnitId: d.businessUnitId });
    setPage(0);
  };
  /** Start over, from the kept booking (QA-723). */
  const startOver = () => {
    setCustomer(null);
    setHold(null);
    setQuery('');
    setNewCustomer(null);
    setBad(null);
    setError(null);
    setD({ ...emptyDraft(opens), businessUnitId: units[0]?.id ?? null });
    setPage(0);
  };
  /** What's typed in the customer field: a change from the one picked lets them go. */
  const typeCustomer = (v: string) => {
    setQuery(v);
    if (customer && v !== customer.displayName) {
      setCustomer(null);
      setHold(null);
      patch({ customerId: null, locationId: null });
    }
  };
  return {
    today,
    nowH,
    mirroring,
    opens,
    plain,
    restored,
    d,
    setD,
    customer,
    setCustomer,
    query,
    setQuery,
    bad,
    setBad,
    invalid,
    found,
    setFound,
    units,
    setUnits,
    types,
    setTypes,
    typesRead,
    setTypesRead,
    techs,
    setTechs,
    board,
    setBoard,
    reads,
    setReads,
    asked,
    page,
    setPage,
    bpForm,
    water,
    setWater,
    capTry,
    setCapTry,
    newLoc,
    setNewLoc,
    estimate,
    setEstimate,
    soldAt,
    setSoldAt,
    booked,
    setBooked,
    textingOff,
    setTextingOff,
    error,
    setError,
    dupe,
    setDupe,
    busy,
    setBusy,
    adding,
    setAdding,
    addError,
    setAddError,
    newCustomer,
    setNewCustomer,
    customerField,
    patch,
    save,
    company,
    setCompany,
    hold,
    setHold,
    pickCustomer,
    unitTypes,
    wet,
    waterSaid,
    answerWater,
    mayAddTypes,
    windowSpan,
    clashes,
    capBase,
    readOf,
    grids,
    soon,
    want,
    wantKey,
    dateRead,
    chosenCap,
    capWhy,
    capWarn,
    pickCell,
    bookNote,
    mayAdjust,
    textTo,
    signedBy,
    stops,
    dayLabel,
    addLocation,
    book,
    bookAnother,
    startOver,
    typeCustomer,
  };
}
export type BookFormState = ReturnType<typeof useBookForm>;

/**
 * The office's own change to the chosen window (BL-057 phase 2), for those who dispatch: close it for
 * the day, or add on-call time, with a reason; or take back a change made. The department's change,
 * when one is picked; the form re-reads the window after (onChanged).
 */
export function useCapacityAdjust({
  date,
  windowStartHour,
  window,
  businessUnitId,
  onChanged,
}: {
  date: string;
  windowStartHour: number;
  window: WindowCap;
  businessUnitId: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'close' | 'add'>('close');
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (action: string, input: object) => {
    setBusy(true);
    setError(null);
    try {
      await client.call(action, input);
      setOpen(false);
      setReason('');
      onChanged();
    } catch (e) {
      setError(e instanceof RpcError ? e.message : 'Couldn’t change it. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const undo = (id: string) => void run('capacity.removeAdjustment', { id });
  const apply = () =>
    void run('capacity.adjust', {
      id: uuidv7(),
      date,
      windowStartHour,
      ...(businessUnitId ? { businessUnitId } : {}),
      ...(kind === 'close' ? { close: true } : { minutes }),
      reason: reason.trim(),
    });
  return {
    open,
    setOpen,
    kind,
    setKind,
    minutes,
    setMinutes,
    reason,
    setReason,
    busy,
    error,
    made: window.adjustments ?? [],
    undo,
    apply,
  };
}

/** BL-129: the company's tags, for tagging the booking; null until read. */
export function useTagList(): { all: TagOption[] | null; failed: boolean } {
  const [all, setAll] = useState<TagOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    client
      .call<{ tags: TagOption[] }>('tags.list', {})
      .then((r) => setAll(r.tags))
      .catch(() => setFailed(true));
  }, []);
  return { all, failed };
}

/**
 * What booking says about the chosen address (records.forLocation): its pinned alerts, then each piece
 * of equipment under a warranty in force, the same check jobs.book makes. Neither stops the booking.
 */
export function useAddressNotes(locationId: string | null) {
  const [r, setR] = useState<{ at: string; records: LocationRecords } | null>(null);
  useEffect(() => {
    if (!locationId) return;
    let live = true;
    void client
      .call<LocationRecords>('records.forLocation', { locationId })
      .then((records) => live && setR({ at: locationId, records }))
      .catch(() => live && setR(null));
    return () => {
      live = false;
    };
  }, [locationId]);
  if (!locationId || !r || r.at !== locationId)
    return { alerts: [] as ReturnType<typeof alertsOf>, warranties: [] as string[] };
  return {
    alerts: alertsOf(r.records),
    warranties: r.records.equipment.map(warrantyLine).filter((x): x is string => !!x),
  };
}
